import { useRef, useState } from 'react'

import { Box, InfiniteScroll, Spinner } from '@factorialco/deprecated-design-system'

import { useActivityLog } from 'modules/core/hooks/useActivityLog'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { LogFilter } from './Filter'
import { LoadingSkeleton } from './Item/LoadingSkeleton'
import { Timeline } from './Timeline'
import { Source } from './types'

const PAGINATION_PER_PAGE = 15

interface Props {
  identifier: string
  parentResourceId?: number
  filteredSources?: Source
  updatesTitle?: string
  upgradeElement?: React.ReactNode
  fromSidepanel?: boolean
  loading?: boolean
}

export const Log = ({
  identifier,
  parentResourceId,
  filteredSources,
  updatesTitle,
  upgradeElement,
  fromSidepanel,
  loading,
}: Props) => {
  const { company } = useCurrentSession()
  const currentSubscriptionPlan = company.subscription.plan

  const {
    loading: loadingLogs,
    logs,
    hasNextPage,
    totalCount,
    refetch,
  } = useActivityLog({
    identifier,
    parentResourceId,
    sources: [],
    first: PAGINATION_PER_PAGE,
  })

  const [sources, setSources] = useState<string[]>([])

  const scrollRef = useRef<HTMLElement>(null)

  useUnsafeEffect(() => {
    refetch({
      identifier,
      parentResourceId,
      sources: sources,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, parentResourceId, sources])

  useEffect(() => {
    track(trackingEvents.activityLog.logActivityVisits, {
      from: identifier,
      company_subscription_plan: currentSubscriptionPlan,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useUnsafeEffect(() => {
    if (sources.length) {
      track(trackingEvents.activityLog.filter, {
        from: identifier,
        filter_type: sources,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources])

  const loadMore = () => {
    const offset = logs.length / PAGINATION_PER_PAGE + 1

    refetch({
      identifier,
      parentResourceId,
      sources: sources,
      first: PAGINATION_PER_PAGE * offset,
    })
  }

  const remainingLogs = totalCount - logs.length >= 5 ? 5 : totalCount - logs.length

  return (
    <Box gap="s8" ref={scrollRef} data-testid="log-content">
      {loading || loadingLogs ? (
        <Box justifyContent="center" width="full" flexDirection="row" padding="s24">
          <Spinner />
        </Box>
      ) : (
        <Box paddingY="s24" position="relative">
          <Box>
            <LogFilter setSources={setSources} value={sources} filteredSources={filteredSources} />
            <InfiniteScroll
              loadMore={loadMore}
              initialLoad={false}
              hasMore={hasNextPage}
              loader={<LoadingSkeleton remainingNumber={remainingLogs} />}
              {...(fromSidepanel && {
                getScrollParent: () => scrollRef?.current?.parentElement || null,
              })}
            >
              <Timeline logs={logs} upgradeElement={upgradeElement} />
            </InfiniteScroll>
          </Box>
        </Box>
      )}
    </Box>
  )
}
